import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import { SCAService } from '@/services/SCAService';

import { closeModal } from '@/store/actions/core.actions';

import { IScaWallet, TLockReason } from '@/store/models/sca.model';
import { ICursorElements } from '@/store/types/search.types';

import { TProfileAction, ProfileActionTypes } from '@/store/types/profile.types';
import { TAppError } from '@/store/types/base.state';
import { TCoreAction } from '@/store/types/modals.types';

export const getSCAWallet = (userId: number): ThunkAction<Promise<void>, {}, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: ProfileActionTypes.GET_SCA,
    userId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.GET_SCA_FAIL,
    userId,
    error,
    shouldSkipToaster: true,
  });
  const success = (scaWallets: Array<IScaWallet>): TProfileAction => ({
    type: ProfileActionTypes.GET_SCA_SUCCESS,
    scaWallets,
    userId,
    shouldSkipToaster: true,
  });

  return async (dispatch: ThunkDispatch<{}, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const scaWallets = await SCAService.getSCAWallets(userId);
      dispatch(success(scaWallets));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const getDeletedSCAWallets = (
  userId: number,
  cursor?: string,
  staleTime?: number,
): ThunkAction<Promise<void>, {}, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: ProfileActionTypes.GET_DELETED_SCA,
    userId,
  });
  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.GET_DELETED_SCA_FAIL,
    userId,
    error,
    shouldSkipToaster: true,
  });
  const success = (scaWallets: ICursorElements<IScaWallet>): TProfileAction => ({
    type: ProfileActionTypes.GET_DELETED_SCA_SUCCESS,
    scaWallets,
    userId,
    shouldSkipToaster: true,
  });

  return async (dispatch: ThunkDispatch<{}, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const scaWallets = await SCAService.getDeletedSCAWallets(userId, cursor, staleTime);
      dispatch(success(scaWallets));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const deleteSCAWallet = (
  userId: number,
  scaWalletId: string,
): ThunkAction<Promise<void>, {}, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: ProfileActionTypes.DELETE_SCA,
    userId,
    scaWalletId,
  });

  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.DELETE_SCA_FAIL,
    userId,
    scaWalletId,
    error,
  });

  const success = (scaWalletId: string): TProfileAction => ({
    type: ProfileActionTypes.DELETE_SCA_SUCCESS,
    scaWalletId,
    userId,
  });

  return async (dispatch: ThunkDispatch<{}, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const { scaWalletId: id } = await SCAService.deleteSCAWallet(scaWalletId);
      dispatch(success(id));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const resetPIN = (userId: number, scaWalletId: string): ThunkAction<Promise<void>, {}, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: ProfileActionTypes.RESET_PIN_SCA,
    userId,
    scaWalletId,
  });

  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.RESET_PIN_SCA_FAIL,
    userId,
    scaWalletId,
    error,
  });

  const success = (scaWallet: IScaWallet): TProfileAction => ({
    type: ProfileActionTypes.RESET_PIN_SCA_SUCCESS,
    scaWallet,
    userId,
  });

  return async (dispatch: ThunkDispatch<{}, {}, TProfileAction>): Promise<void> => {
    dispatch(request());
    try {
      const scaWallet = await SCAService.resetPIN(scaWalletId);
      dispatch(success(scaWallet));
    } catch (error) {
      dispatch(fail(error));
    }
  };
};

export const unlockSCA = (userId: number, scaWalletId: string): ThunkAction<Promise<void>, {}, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: ProfileActionTypes.UNLOCK_SCA,
    scaWalletId,
    userId,
  });

  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.UNLOCK_SCA_FAIL,
    error,
    userId,
    scaWalletId,
  });

  const success = (scaWallet: IScaWallet): TProfileAction => ({
    type: ProfileActionTypes.UNLOCK_SCA_SUCCESS,
    userId,
    scaWallet,
  });

  return async (dispach: ThunkDispatch<{}, {}, TProfileAction>): Promise<void> => {
    dispach(request());
    try {
      const scaWallet = await SCAService.unlockSCAWallet(scaWalletId);
      dispach(success(scaWallet));
    } catch (error) {
      dispach(fail(error));
    }
  };
};

export const lockSCAWallet = (
  userId: number,
  scaWalletId: string,
  lockReason: TLockReason,
  lockMessage?: string,
): ThunkAction<Promise<void>, {}, {}, TProfileAction> => {
  const request = (): TProfileAction => ({
    type: ProfileActionTypes.LOCK_SCA,
    userId,
    scaWalletId,
  });

  const fail = (error: TAppError): TProfileAction => ({
    type: ProfileActionTypes.LOCK_SCA_FAIL,
    userId,
    scaWalletId,
    error,
  });

  const success = (scaWallet: IScaWallet): TProfileAction => ({
    type: ProfileActionTypes.LOCK_SCA_SUCCESS,
    scaWallet,
    userId,
  });

  return async (dispatch: ThunkDispatch<{}, {}, TProfileAction | TCoreAction>): Promise<void> => {
    dispatch(request());
    try {
      const scaWallet = await SCAService.secureSCAWallet(scaWalletId, lockReason, lockMessage);
      dispatch(success(scaWallet));
    } catch (error) {
      dispatch(fail(error));
    } finally {
      dispatch(closeModal());
    }
  };
};
